import { useEffect, useState } from 'react';
import {
    AccountLayout,
    // getAssociatedTokenAddress,
    // unpackMint
} from '@solana/spl-token';
import {
    BigNumberish,
    buildSimpleTransaction,
    ComputeBudgetConfig,
    Currency,
    CurrencyAmount,
    InnerSimpleV0Transaction,
    Liquidity,
    LOOKUP_TABLE_CACHE,
    // Percent,
    // SPL_ACCOUNT_LAYOUT,
    Token,
    TOKEN_PROGRAM_ID,
    TokenAmount,
    TxVersion,
    // WSOL,
} from '@raydium-io/raydium-sdk';
import {
    // AccountInfo,
    // Commitment,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    ParsedInstruction,
    ParsedTransactionWithMeta,
    PartiallyDecodedInstruction,
    PublicKey,
    SendOptions,
    Signer,
    SystemProgram,
    Transaction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useSplToken } from '../../hooks/useSplToken';
import { useJito } from '../../hooks/useJito';

import getSleepTime from '../../utils/get-sleep-time';
import sleep from '../../utils/sleep';
import { formatAmmKeysById } from '../../utils/format-amm-keys-by-id';
import { useAutomatizationGateway } from '../../hooks/useAutomatizationGateway';
import { ClientCallback, ClientLog, Event, LogLevel, Role } from '@sodamnfoolish/spl-factory-backend-automatization';
import { bs58 } from '@project-serum/anchor/dist/cjs/utils/bytes';
import '../../RemoveLiquidity.css';
import {
    removeLiquidityAutoSwapPriorityGas,
    removeLiquidityBurnLpPriorityGas,
    removeLiquidityJitoTip,
    removeLiquidityManualRemovePriorityFee,
    removeLiquidityManualSwapPriorityGas,
    removeLiquidityScanningPriorityGas,
    removeLiquidityTimedManualSwapPriorityGas,
} from '../../../ConfigFees';
import { bigIntToStringWithDecimals } from '../../utils/bigInt-to-string-with-decimals';
// import { useRaydium } from '../hooks/useRaydium';

// const FETCH_INTERVAL = 2000;

export function RemoveLiquidity({
    wallet,
    mainConnection,
    connections,
    walletLoaded,
    poolBalance,
    setPoolBalance,
    totalWalletsBalance,
    logMessage,
    deployedToken,
    updatedAt,
    autoAction,
    injectToken,
    volFunder,
    // allWallets,
}: {
    wallet: Keypair;
    mainConnection: Connection;
    connections: Connection[];
    walletLoaded: boolean;
    poolBalance: string;
    setPoolBalance: (poolBalance: string) => void;
    totalWalletsBalance: number;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    injectToken: (address: string) => Promise<void>;
    deployedToken: string;
    updatedAt: string;
    autoAction: string;
    volFunder: Keypair | undefined;
    // allWallets: Keypair[];
}) {
    const { getWalletTokenAccounts, getTokenDecimals, burnToken } = useSplToken();
    const { spamAndConfirmBundle } = useJito();
    const { automatizationClient } = useAutomatizationGateway((role) => role == Role.Scanner);
    // const { getMinAmountOutV2 } = useRaydium();

    const [isJitoTx, setIsJitoTx] = useState<boolean>(true);
    const [jitoTip, setJitoTip] = useState<number>(removeLiquidityJitoTip / LAMPORTS_PER_SOL);
    const [futureDeploy, setFutureDeploy] = useState<boolean>(true);

    const [deployPoolSignature, setDeployPoolSignature] = useState<string>('');
    const [removeLpAmount, setRemoveLpAmount] = useState<string>('0');
    const [repeatsNumber, setRepeatsNumber] = useState<number>(3);
    const [startTimeEpoch, setStartTimeEpoch] = useState<number>(0);

    const [autoRemoveDelay, setAutoRemoveDelay] = useState<number>(0);

    const [takeProfitValue, setTakeProfitValue] = useState<number>(7);
    const [burnConfirmation, setBurnConfirmation] = useState<boolean>(false);

    const [amountIn, setAmountIn] = useState<string>('0');

    const [autoSell, setAutoSell] = useState<boolean>(true);

    const [autoSellPercent, setAutoSellPercent] = useState<number>(0.5);
    const [autoSellMs, setAutoSellMs] = useState<number>(333);
    const [autoSellRepeats, setAutoSellRepeats] = useState<number>(150);
    const [autoSellSpam, setAutoSellSpam] = useState<number>(1);

    const [burnLpAmount, setBurnLpAmount] = useState<number>(0);

    const [multiSellStart, setMultiSellStart] = useState<number>(0);

    useEffect(() => {
        if (!automatizationClient) return;

        automatizationClient.on(Event.PreStart, async (address: string, callback: ClientCallback) => {
            await injectToken(address).catch((reason) => {
                callback([new ClientLog(LogLevel.Error, `Error`)]);
                throw reason;
            });
            callback([new ClientLog(LogLevel.Info, `Address received ${address}`)]);
        });

        automatizationClient.on(Event.StartTrading, (_: string, callback: ClientCallback) => {
            startPoolBalanceScanning(0, BigInt(Number.MAX_VALUE)).catch((reason) => {
                callback([new ClientLog(LogLevel.Error, 'Error')]);
                throw reason;
            });
            setTimeout(() => callback([new ClientLog(LogLevel.Info, 'Started')]), 1000);
        });

        automatizationClient.on(Event.RemoveLiquidity, async (_: string, callback: ClientCallback) => {
            await removeLiquidity(deployPoolSignature, removeLpAmount, repeatsNumber, startTimeEpoch, removeLiquidityManualRemovePriorityFee).catch(
                (reason) => {
                    callback([new ClientLog(LogLevel.Error, 'Error')]);
                    throw reason;
                },
            );
            callback([new ClientLog(LogLevel.Info, 'Liquidity removed')]);
            window.location.reload();
        });

        automatizationClient.on(Event.StopFreezer, (address: string, callback: ClientCallback) => {
            callback([new ClientLog(LogLevel.Info, 'Stopped')]);
            window.location.reload();
        });

        return () => {
            automatizationClient.off(Event.PreStart);
        };
    }, [automatizationClient]);

    // const [holdersConnection, setHoldersConnection] = useState(connections[connections.length - 1]);
    // const [tokensUi, setTokensUi] = useState<string>();
    // const [tokensPercentageUi, setTokensPercentageUi] = useState<string>();
    // const [valueUi, setValueUi] = useState<string>();
    //
    // useEffect(() => {
    //     const fetch = async () => {
    //         try {
    //             if (!deployedToken || deployedToken == '' || !deployPoolSignature || deployPoolSignature == '') return;
    //
    //             const mintPubkey = new PublicKey(deployedToken);
    //
    //             const poolKeys = await getPoolKeys(
    //                 await holdersConnection.getParsedTransaction(deployPoolSignature, {
    //                     commitment: 'confirmed',
    //                     maxSupportedTransactionVersion: 0,
    //                 }),
    //             );
    //
    //             const raydiumAtaPubkey = poolKeys.quoteMint.toBase58() == deployedToken ? poolKeys.quoteVault : poolKeys.baseVault;
    //
    //             const walletAtasPubkey = await Promise.all(allWallets.map((wallet) => getAssociatedTokenAddress(mintPubkey, wallet.publicKey)));
    //
    //             const getMultipleAccountsInfoChunked = async (pubKeys: Array<PublicKey>, commitment: Commitment = 'processed', chunkSize = 100) => {
    //                 const result = [];
    //
    //                 for (let i = 0; i < pubKeys.length; i += chunkSize)
    //                     result.push(await holdersConnection.getMultipleAccountsInfo(pubKeys.slice(i, i + chunkSize), commitment));
    //
    //                 return result.flat();
    //             };
    //
    //             const accountInfos = await getMultipleAccountsInfoChunked([...walletAtasPubkey, raydiumAtaPubkey, mintPubkey]);
    //
    //             const mintAccountInfo = accountInfos.pop()!;
    //             const raydiumAtaInfo = accountInfos.pop();
    //
    //             const walletAtaInfos = accountInfos;
    //
    //             const mint = unpackMint(mintPubkey, mintAccountInfo);
    //
    //             const unpackAccountBalance = (info: AccountInfo<Buffer> | null | undefined) =>
    //                 BigInt(info ? SPL_ACCOUNT_LAYOUT.decode(info.data).amount.toString() : 0);
    //
    //             const raydiumBalance = unpackAccountBalance(raydiumAtaInfo);
    //
    //             const walletBalances = walletAtaInfos.map((info) => unpackAccountBalance(info));
    //
    //             const sumOfWalletBalances = walletBalances.reduce((sum, balance) => sum + balance, BigInt(0));
    //
    //             const tokens = mint.supply - raydiumBalance - sumOfWalletBalances;
    //
    //             const value = await getMinAmountOutV2(
    //                 holdersConnection,
    //                 Number(tokens),
    //                 poolKeys,
    //                 mintPubkey,
    //                 new PublicKey(WSOL.mint),
    //                 mint.decimals,
    //                 WSOL.decimals,
    //                 new Percent(0, 100),
    //             );
    //
    //             setTokensUi((Number(tokens) / 10 ** mint.decimals).toFixed(0));
    //             setTokensPercentageUi(((Number(tokens) / Number(mint.supply)) * 100).toFixed(0));
    //             setValueUi(value.toFixed(2));
    //         } catch (error: any) {
    //             console.error(error.message || JSON.stringify(error));
    //         }
    //     };
    //
    //     const interval = setInterval(() => setTimeout(fetch, 1000 - (Date.now() % 1000)), 1000);
    //
    //     return () => {
    //         clearInterval(interval);
    //     };
    // }, [deployedToken, deployPoolSignature, allWallets, holdersConnection]);

    //const swapTokenJitoTip = 0.0001;

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);
    useEffect(() => {
        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        if (!autoActionStarted && timeDifference < 30000) {
            if (autoAction === 'poolScanner') {
                logMessage('Starting auto action for pool scanner.');
                setAutoActionStarted(true);
                startPoolBalanceScanning(0, BigInt(Number.MAX_VALUE));
            } else if (autoAction === 'tSell') {
                logMessage('Starting auto action for remove liquidity.');
                setAutoActionStarted(true);
                scanLpBalance();
            }
        }
    }, [deployedToken, autoAction]);

    const isJitoTxChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setIsJitoTx(checked);
    };
    const jitoTipChange = (event: { target: { value: string } }) => {
        setJitoTip(Number(event.target.value));
    };
    const isFutureDeployChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setFutureDeploy(checked);
    };

    const deployPoolSignatureChange = (event: { target: { value: string } }) => {
        setDeployPoolSignature(event.target.value);
    };

    const removeLpAmountChange = (event: { target: { value: string } }) => {
        setRemoveLpAmount(event.target.value);
    };

    const autoRemoveDelayChange = (event: { target: { value: string } }) => {
        setAutoRemoveDelay(Number(event.target.value));
    };

    const amountInChange = (event: { target: { value: string } }) => {
        setAmountIn(event.target.value);
    };

    const startTimeEpochChange = (event: { target: { value: string } }) => {
        setStartTimeEpoch(Number(event.target.value));
    };

    const repeatsNumberChange = (event: { target: { value: string } }) => {
        setRepeatsNumber(Number(event.target.value));
    };

    const takeProfitValueChange = (event: { target: { value: string } }) => {
        setTakeProfitValue(Number(event.target.value));
    };

    const burnConfirmationChagne = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setBurnConfirmation(checked);
    };

    const autoSellChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAutoSell(checked);
    };
    const autoSellPercentChange = (event: { target: { value: string } }) => {
        setAutoSellPercent(Number(event.target.value));
    };
    const autoSellMsChange = (event: { target: { value: string } }) => {
        setAutoSellMs(Number(event.target.value));
    };
    const autoSellRepeatsChange = (event: { target: { value: string } }) => {
        setAutoSellRepeats(Number(event.target.value));
    };
    const autoSellSpamChange = (event: { target: { value: string } }) => {
        setAutoSellSpam(Number(event.target.value));
    };

    const burnLpAmountChange = (event: { target: { value: string } }) => {
        setBurnLpAmount(Number(event.target.value));
    };

    const multiSellStartChange = (event: { target: { value: string } }) => {
        setMultiSellStart(Number(event.target.value));
    };

    function getDeployPoolAccountsFromInstructions(deployPoolTransactionInstructions: (ParsedInstruction | PartiallyDecodedInstruction)[]) {
        let deployPoolAccounts4;
        for (let i = 2; i <= 4; i++) {
            try {
                deployPoolAccounts4 = (deployPoolTransactionInstructions[i] as unknown as PartiallyDecodedInstruction).accounts;
                const serumAccount = deployPoolAccounts4[15].toBase58();
                if (serumAccount === 'srmqPvymJeFKQ4zGQed1GFppgkRHL9kaELCbyksJtPX') {
                    return deployPoolAccounts4;
                }
            } catch {
                continue;
            }
        }

        throw new Error('Failed to get deploy pool accounts');
    }

    async function getBalance(deployPoolSignature: string) {
        console.log('deployPoolSignature', deployPoolSignature);
        if (walletLoaded === false) {
            logMessage('WALLET IS NOT LOADED! ');
            return BigInt(0);
        }
        let transaction: ParsedTransactionWithMeta | null = null;
        for (let i = 0; transaction === null; i++) {
            if (i > 0) {
                logMessage('RPC RETURNED NULL ON TRANSACTION REQUEST ');
                await sleep(50);
            }

            transaction = await mainConnection.getParsedTransaction(deployPoolSignature, {
                commitment: 'confirmed',
                maxSupportedTransactionVersion: 0,
            });

            if (i >= 5) {
                logMessage('Failed.');
                return BigInt(0);
            }
        }
        const deployPoolTransactionInstructions = transaction!.transaction.message.instructions;

        const deployPoolAccounts4 = getDeployPoolAccountsFromInstructions(deployPoolTransactionInstructions);
        const lpMint = deployPoolAccounts4[7];

        let response = await mainConnection.getTokenAccountsByOwner(
            wallet.publicKey,
            {
                mint: lpMint,
            },
            'processed',
        );
        let walletLpBalance = BigInt(0);
        response.value.forEach((e) => {
            const accountInfo = AccountLayout.decode(e.account.data);
            walletLpBalance = accountInfo.amount;
        });

        logMessage('Your LP balance: ' + walletLpBalance.toString() + ' ');
        setBurnLpAmount(Number(walletLpBalance));
        setRemoveLpAmount(walletLpBalance.toString());
        const decreasedWalletLpBalance = BigInt(Math.floor((Number(walletLpBalance) / 1000) * 999));

        return decreasedWalletLpBalance;
    }

    async function sendTx(
        connection: Connection,
        payer: Keypair | Signer,
        txs: (VersionedTransaction | Transaction)[],
        startTimeEpochMs: number,
        options?: SendOptions,
    ): Promise<string[]> {
        const txids: string[] = [];

        logMessage('Transaction is ready to go! ');

        const sleepTime = getSleepTime(startTimeEpochMs);
        if (sleepTime > 0) logMessage('Sleep ! ' + sleepTime.toString() + ' ms to start');
        await sleep(sleepTime);

        for (const iTx of txs) {
            if (iTx instanceof VersionedTransaction) {
                iTx.sign([payer]);
                txids.push(await connection.sendTransaction(iTx, options));
            } else {
                txids.push(await connection.sendTransaction(iTx, [payer], options));
            }
            //logMessage('TXs SENT ' + txids[txids.length - 1] + ' ' + getDateString());
        }
        return txids;
    }

    async function buildAndSendTx(
        innerSimpleV0Transaction: InnerSimpleV0Transaction[],
        startTimeEpochMs: number,
        connection: Connection,
        options?: SendOptions,
        makeTxVersion = TxVersion.V0,
        addLookupTableInfo = LOOKUP_TABLE_CACHE,
    ) {
        const willSendTx = await buildSimpleTransaction({
            connection,
            makeTxVersion,
            payer: wallet.publicKey,
            innerTransactions: innerSimpleV0Transaction,
            addLookupTableInfo: addLookupTableInfo,
        });

        return (await sendTx(connection, wallet, willSendTx, startTimeEpochMs, options))[0];
    }

    async function removeLiquidity(deployPoolSignature: string, removeLpAmount: string, repeatsNumber: number, startTimeEpochMs: number, priorityGas: number) {
        try {
            if (walletLoaded === false) {
                logMessage('ERROR: WALLET IS NOT LOADED!');
                return;
            }

            if (removeLpAmount === '0') {
                logMessage('LP AMOUNT IS 0!', 'error');
                return;
            }
            if (startTimeEpochMs !== 0 && startTimeEpochMs.toString().length < 13) {
                logMessage(`ERROR: TIME IS INVALID! ${startTimeEpochMs.toString()}`);
                return;
            }
            const initTime = Date.now();
            const delay = getSleepTime(startTimeEpochMs, true);
            logMessage('Sleep ! ' + delay.toString() + ' ms to start preparing tx');
            await sleep(delay);

            logMessage('Preparing transaction ');
            const transaction = await mainConnection.getParsedTransaction(deployPoolSignature, {
                commitment: 'confirmed',
                maxSupportedTransactionVersion: 0,
            });

            const deployPoolTransactionInstructions = transaction!.transaction.message.instructions;

            let lpTokenPublicKey = getDeployPoolAccountsFromInstructions(deployPoolTransactionInstructions)[7].toString();

            const lpTokenDecimals = await getTokenDecimals(mainConnection, new PublicKey(lpTokenPublicKey));
            const lpToken = new Token(TOKEN_PROGRAM_ID, new PublicKey(lpTokenPublicKey), lpTokenDecimals);
            const removeLpTokenAmount = new TokenAmount(lpToken, removeLpAmount);

            logMessage(`Amount of lp to burn without decimals ${removeLpAmount}`);

            const walletTokenAccounts = await getWalletTokenAccounts(mainConnection, wallet.publicKey);

            const poolKeys = await getPoolKeys(transaction);
            const computeBudgetConfig: ComputeBudgetConfig = {
                microLamports: priorityGas, //14000000
            };
            for (let j = 0; j < (isJitoTx ? 1 : repeatsNumber); j++) {
                const response = await Liquidity.makeRemoveLiquidityInstructionSimple({
                    connection: mainConnection,
                    poolKeys,
                    userKeys: {
                        owner: wallet.publicKey,
                        payer: wallet.publicKey,
                        tokenAccounts: walletTokenAccounts,
                    },
                    amountIn: removeLpTokenAmount,
                    makeTxVersion: TxVersion.V0,
                    computeBudgetConfig,
                });
                if (isJitoTx) {
                    let transferVersionedTransaction: VersionedTransaction | undefined = undefined;
                    if (volFunder) {
                        const volFunderBalance = await mainConnection.getBalance(volFunder.publicKey, 'processed');
                        if (volFunderBalance) {
                            logMessage(`VolFunder balance found: ${volFunderBalance}. Including transfer tx into bundle.`);
                            const transferInstruction = SystemProgram.transfer({
                                fromPubkey: volFunder.publicKey,
                                toPubkey: wallet.publicKey,
                                lamports: volFunderBalance,
                            });

                            const message = new TransactionMessage({
                                payerKey: wallet.publicKey,
                                recentBlockhash: (await mainConnection.getLatestBlockhash()).blockhash,
                                instructions: [transferInstruction],
                            }).compileToV0Message();

                            transferVersionedTransaction = new VersionedTransaction(message);
                            transferVersionedTransaction.sign([volFunder, wallet]);
                        }
                    }

                    const tx = (
                        await buildSimpleTransaction({
                            connection: mainConnection,
                            makeTxVersion: TxVersion.V0,
                            payer: wallet.publicKey,
                            innerTransactions: response.innerTransactions,
                            addLookupTableInfo: LOOKUP_TABLE_CACHE,
                        })
                    )[0];
                    if (tx instanceof VersionedTransaction) {
                        const sleepTime = getSleepTime(startTimeEpochMs);
                        await sleep(sleepTime);
                        logMessage('Sending bundle ');
                        tx.sign([wallet]);
                        const transactionsToSend = transferVersionedTransaction ? [transferVersionedTransaction, tx] : [tx];
                        const bundleId = await spamAndConfirmBundle(mainConnection, transactionsToSend, wallet, jitoTip * LAMPORTS_PER_SOL);

                        if (autoSell && j === 0) {
                            const { blockhash, lastValidBlockHeight } = await mainConnection.getLatestBlockhash('confirmed');
                            await mainConnection.confirmTransaction(
                                {
                                    blockhash: blockhash,
                                    lastValidBlockHeight: lastValidBlockHeight,
                                    signature: bs58.encode(transactionsToSend[0].signatures[0]),
                                },
                                'processed',
                            );
                            multiSell(deployPoolSignature, removeLiquidityAutoSwapPriorityGas, 0);
                        }
                        logMessage(`Bundle ID: ${bundleId}`);
                    } else {
                        logMessage(`ERROR: Failed create transaction`);
                    }
                } else {
                    const transactions = await Promise.all(
                        connections.map((connection) => buildAndSendTx(response.innerTransactions, startTimeEpochMs, connection, { skipPreflight: true })),
                    );

                    logMessage('RPCs responded ');
                    transactions.forEach((tx, i) => {
                        logMessage(`RPC${i}: transaction ${tx} sent`);
                    });
                }
            }

            const workTime = Date.now() - initTime;
            logMessage(`Execution time: ${(workTime / 1000).toString()} seconds.`);
        } catch (error) {
            logMessage(`Failed to remove liquidity ${error}`);
        }
    }

    //trasaction length should be equal to 3
    async function getPoolKeys(transaction: ParsedTransactionWithMeta | null) {
        if (transaction === null) throw new Error('deploy pool transaction is null');

        const deployPoolTransactionInstructions = transaction.transaction.message.instructions;
        const ammId = getDeployPoolAccountsFromInstructions(deployPoolTransactionInstructions)[4];

        const poolKeys = await formatAmmKeysById(mainConnection, ammId);

        return poolKeys;
    }

    async function getSignatures(until: string | undefined): Promise<string[]> {
        if (walletLoaded === false) {
            logMessage('ERROR: WALLET IS NOT LOADED! ');
            return [];
        }

        const signaturesInfo = await mainConnection.getSignaturesForAddress(wallet.publicKey, { limit: 100, until: until }, 'confirmed');
        const signaturesArray: string[] = signaturesInfo.map((signatureInfo) => signatureInfo.signature);

        let deployPoolSignature = '';

        const transactions = await mainConnection.getParsedTransactions(signaturesArray, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0,
        });

        let startTime = '0';
        for (const [index, transaction] of transactions.entries()) {
            if (deployPoolSignature === '') {
                try {
                    const deployPoolTransactionInstructions = transaction!.transaction.message.instructions;
                    const account = getDeployPoolAccountsFromInstructions(deployPoolTransactionInstructions)[15].toBase58();

                    deployPoolSignature = signaturesArray[index];
                    setDeployPoolSignature(signaturesArray[index]);
                    const intructions = transaction!.meta?.logMessages;
                    intructions!.forEach((instruction, i) => {
                        if (instruction.includes('open_time')) {
                            const regex = /open_time:\s*(\d+)/;
                            const match = regex.exec(instruction);
                            if (match && match[1]) {
                                startTime = (parseInt(match[1], 10) * 1000).toString();
                                console.log(startTime);
                                setStartTimeEpoch(Number(startTime));
                            }
                        }
                    });
                    break;
                } catch {}
            }
            //}
        }
        //logMessage(getDateString());
        return [deployPoolSignature, startTime];
    }

    async function startPoolBalanceScanning(extraDelayAfterStart: number, takeProfitValue: bigint) {
        logMessage('Start Remove LP scanner... ');

        let until: string | undefined = undefined;
        if (futureDeploy) {
            until = (await mainConnection.getSignaturesForAddress(wallet.publicKey, { limit: 1 }, 'confirmed'))[0].signature;
            console.log('Searching after:', until);
        }
        while (true) {
            try {
                const [deployPoolSign, startTimestamp] =
                    deployPoolSignature !== '' && startTimeEpoch !== 0 ? [deployPoolSignature, startTimeEpoch] : await getSignatures(until);
                if (deployPoolSign !== '') {
                    const lpBalance = (await getBalance(deployPoolSign)).toString();
                    logMessage(`Scanning: You have ${lpBalance} LP tokens.`);
                    scanPoolBalance(
                        deployPoolSign,
                        lpBalance,
                        repeatsNumber,
                        Number(startTimestamp) + extraDelayAfterStart,
                        removeLiquidityScanningPriorityGas,
                        takeProfitValue,
                    );

                    break;
                }
            } catch (error) {
                logMessage(`Failed to start pool balance scanning ${error}`);
            }
            await sleep(70);
        }
    }

    async function scanPoolBalance(
        deployPoolSign: string,
        lpBalance: string,
        repeatsNumber: number,
        start: number,
        priorityGas: number,
        takeProfitValue: bigint,
    ) {
        logMessage('WS');

        const quotePoolId = await getQuotePoolByDeploymentSignature(deployPoolSign);
        const pow = 9;
        let removed = false;
        const scanPoolQuoteBalanceSubId = mainConnection.onAccountChange(
            quotePoolId,
            (updatedAccountInfo) => {
                try {
                    const decodedTokenAccountInfo = AccountLayout.decode(updatedAccountInfo.data);
                    const balanceBigInt = decodedTokenAccountInfo.amount;
                    const balanceString = bigIntToStringWithDecimals(balanceBigInt, pow);
                    setPoolBalance(balanceString);
                    if (balanceBigInt >= takeProfitValue) {
                        if (!removed) {
                            logMessage('Taking profit');
                            removed = true;
                            removeLiquidity(deployPoolSign, lpBalance, repeatsNumber, start, priorityGas);
                        }
                    }
                } catch (error) {
                    logMessage(`Failed to update pool balance ${error}`);
                }
            },
            'processed',
        );
        try {
            const accountInfo = await mainConnection.getAccountInfo(quotePoolId, 'processed');
            const decodedTokenAccountInfo = AccountLayout.decode(accountInfo!.data);
            const balanceBigInt = decodedTokenAccountInfo.amount;
            const balanceString = bigIntToStringWithDecimals(balanceBigInt, pow);
            setPoolBalance(balanceString);
            if (balanceBigInt >= takeProfitValue) {
                removeLiquidity(deployPoolSign, lpBalance, repeatsNumber, start, priorityGas);
                mainConnection.removeAccountChangeListener(scanPoolQuoteBalanceSubId);
            }
        } catch (error) {
            logMessage(`Failed to update pool balance ${error}`);
        }
    }

    async function getQuotePoolByDeploymentSignature(deployPoolSignature: string) {
        let transaction: ParsedTransactionWithMeta | null = null;
        for (let i = 0; transaction === null; i++) {
            if (i > 0) {
                logMessage('RPC RETURNED NULL ON TRANSACTION REQUEST ');
                await sleep(50);
            }
            transaction = await mainConnection.getParsedTransaction(deployPoolSignature, { commitment: 'confirmed', maxSupportedTransactionVersion: 0 });
        }
        const deployPoolTransactionInstructions = transaction!.transaction.message.instructions;

        const quotePool = getDeployPoolAccountsFromInstructions(deployPoolTransactionInstructions)[11];

        return quotePool;
    }

    async function multiSell(deployPoolSignature: string, priorityGas: number, startTime: number) {
        try {
            const lpMint = await getlpMintByDeploymentSignature(deployPoolSignature);
            logMessage('Start multi sells ');
            const lpTokenAccount = (
                await mainConnection.getParsedTokenAccountsByOwner(
                    wallet.publicKey,
                    {
                        mint: lpMint,
                    },
                    'processed',
                )
            ).value[0].pubkey;
            const lpAccountInfo = await mainConnection.getAccountInfo(lpTokenAccount, 'processed');
            const decodedLpTokenAccountInfo = AccountLayout.decode(lpAccountInfo!.data);
            console.log('decodedLpTokenAccountInfo');
            const lpBalanceBigInt = decodedLpTokenAccountInfo.amount;
            console.log('YOUR LP BALANCE:' + lpBalanceBigInt.toString());
            const sleepTime = getSleepTime(startTime);
            if (sleepTime > 0) {
                logMessage(`Sleeping ${sleepTime.toString()} ms to start `);
            }
            await sleep(sleepTime);

            const sellsReadyTime = Date.now();
            for (let i = 0; i < autoSellRepeats; i++) {
                for (let j = 0; j < autoSellSpam; j++) {
                    swapCustomPercent(deployPoolSignature, autoSellPercent, priorityGas);
                }
                const sleepTime = getSleepTime(sellsReadyTime + i * autoSellMs);
                if (sleepTime > 0) logMessage('Sleep ' + sleepTime.toString() + ' ms for new sell');
                await sleep(sleepTime);
            }
        } catch (error) {
            logMessage(`Failed to multi sell ${error}`);
        }
    }

    async function scanLpBalance() {
        logMessage('Start Token Selling scanner... ');

        while (true) {
            const [deployPoolSign] = deployPoolSignature !== '' ? [deployPoolSignature] : await getSignatures(undefined);

            console.log(deployPoolSign);
            if (deployPoolSign !== '') {
                const lpBalance = await getBalance(deployPoolSign);
                console.log('AUTOSELLER: Initial LP balance: ' + lpBalance.toString());
                scanUserLpBalance(deployPoolSign, lpBalance, removeLiquidityAutoSwapPriorityGas);
                break;
            }
            await sleep(170);
        }
    }

    async function scanUserLpBalance(deployPoolSign: string, sellThreshold: bigint, priorityGas: number) {
        const lpMint = await getlpMintByDeploymentSignature(deployPoolSign);
        const lpTokenAccount = (
            await mainConnection.getParsedTokenAccountsByOwner(
                wallet.publicKey,
                {
                    mint: lpMint,
                },
                'processed',
            )
        ).value[0].pubkey;
        const scanLpBalanceSubId = mainConnection.onAccountChange(
            lpTokenAccount,
            async (updatedAccountInfo) => {
                try {
                    const decodedLpTokenAccountInfo = AccountLayout.decode(updatedAccountInfo.data);
                    const lpBalanceBigInt = decodedLpTokenAccountInfo.amount;
                    if (lpBalanceBigInt < sellThreshold) {
                        console.log('YOUR LP BALANCE:' + lpBalanceBigInt.toString());
                        mainConnection.removeAccountChangeListener(scanLpBalanceSubId);
                        const startTime = Date.now();
                        for (let i = 0; i < autoSellRepeats; i++) {
                            const currrentPriorityGas = i <= 10 ? priorityGas * 10 : priorityGas;
                            for (let j = 0; j < autoSellSpam; j++) {
                                swapCustomPercent(deployPoolSign, autoSellPercent, currrentPriorityGas);
                            }
                            const sleepTime = getSleepTime(startTime + i * autoSellMs);
                            if (sleepTime > 0) logMessage('Sleep ' + sleepTime.toString() + ' ms for new sell');
                            await sleep(sleepTime);
                        }
                    }
                } catch (error) {
                    logMessage(`Failed to update user LP balance ${error}`);
                }
            },
            'processed',
        );
        const lpAccountInfo = await mainConnection.getAccountInfo(lpTokenAccount, 'processed');
        const decodedLpTokenAccountInfo = AccountLayout.decode(lpAccountInfo!.data);
        const lpBalanceBigInt = decodedLpTokenAccountInfo.amount;
        if (lpBalanceBigInt < BigInt(100)) {
            mainConnection.removeAccountChangeListener(scanLpBalanceSubId);
            console.log('YOUR LP BALANCE:' + lpBalanceBigInt.toString());
            const startTime = Date.now();
            for (let i = 0; i < autoSellRepeats; i++) {
                const currrentPriorityGas = i <= 10 ? priorityGas * 10 : priorityGas;
                for (let j = 0; j < autoSellSpam; j++) {
                    swapCustomPercent(deployPoolSign, autoSellPercent, currrentPriorityGas);
                }
                const sleepTime = getSleepTime(startTime + i * autoSellMs);
                if (sleepTime > 0) logMessage('Sleep ' + sleepTime.toString() + ' ms for new sell');
                await sleep(sleepTime);
            }
        }
    }

    async function getlpMintByDeploymentSignature(deployPoolSignature: string) {
        let transaction: ParsedTransactionWithMeta | null = null;
        for (let i = 0; transaction === null; i++) {
            if (i > 0) {
                logMessage('RPC RETURNED NULL ON TRANSACTION REQUEST ');
                await sleep(50);
            }
            transaction = await mainConnection.getParsedTransaction(deployPoolSignature, { commitment: 'confirmed', maxSupportedTransactionVersion: 0 });
        }
        const deployPoolTransactionInstructions = transaction!.transaction.message.instructions;
        let lpMint;
        try {
            const deployPoolAccounts4 = (deployPoolTransactionInstructions[2] as unknown as PartiallyDecodedInstruction).accounts;
            lpMint = deployPoolAccounts4[7];
        } catch {
            const deployPoolAccounts4 = (deployPoolTransactionInstructions[3] as unknown as PartiallyDecodedInstruction).accounts;
            lpMint = deployPoolAccounts4[7];
        }

        return lpMint;
    }

    async function swapCustom(deployPoolSignature: string, amountIn: BigNumberish, priorityGas: number) {
        if (walletLoaded === false) {
            logMessage('ERROR: Wallet not loaded. ');
            return;
        }
        if (deployPoolSignature === '') {
            logMessage('ERROR:Invalid deployment signature');
            return;
        }
        try {
            const transaction = await mainConnection.getParsedTransaction(deployPoolSignature, {
                commitment: 'confirmed',
                maxSupportedTransactionVersion: 0,
            });
            const poolKeys = await getPoolKeys(transaction);

            const walletTokenAccounts = await getWalletTokenAccounts(mainConnection, wallet.publicKey);

            const baseTokenDecimals = await getTokenDecimals(mainConnection, poolKeys.baseMint);
            const baseToken = new Token(TOKEN_PROGRAM_ID, new PublicKey(poolKeys.baseMint.toString()), baseTokenDecimals);
            const baseTokenSwapAmount = new TokenAmount(baseToken, amountIn);

            const quoteTokenDecimals = await getTokenDecimals(mainConnection, poolKeys.quoteMint);
            const quoteCurrency = new Currency(quoteTokenDecimals);
            const quoteCurrencyAmount = new CurrencyAmount(quoteCurrency, 1);

            const computeBudgetConfig: ComputeBudgetConfig = {
                microLamports: priorityGas, //4000
            };

            const response = await Liquidity.makeSwapInstructionSimple({
                connection: mainConnection,
                poolKeys: poolKeys,
                userKeys: {
                    tokenAccounts: walletTokenAccounts,
                    owner: wallet.publicKey,
                },
                amountIn: baseTokenSwapAmount,
                amountOut: quoteCurrencyAmount,
                fixedSide: 'in',
                makeTxVersion: TxVersion.V0,
                computeBudgetConfig: computeBudgetConfig,
            });

            const swapVersionedTransaction = (
                await buildSimpleTransaction({
                    connection: mainConnection,
                    makeTxVersion: TxVersion.V0,
                    payer: wallet.publicKey,
                    innerTransactions: response.innerTransactions,
                    addLookupTableInfo: LOOKUP_TABLE_CACHE,
                })
            )[0] as unknown as VersionedTransaction;
            swapVersionedTransaction.sign([wallet]);

            const signature = await mainConnection.sendRawTransaction(swapVersionedTransaction.serialize(), { skipPreflight: true });
            logMessage(`Swap sent: ${signature}`);
            //const bundleID = await sendBundle(mainConnection, [swapVersionedTransaction], wallet, swapTokenJitoTip * LAMPORTS_PER_SOL);
            //logMessage(`Sell token bundle ID: ${bundleID}`);
        } catch (error) {
            logMessage('Failed to swap', 'error');
            console.log('Failed to swap', error);
        }
    }

    async function getBaseTokenBalance(deployPoolSignature: string) {
        if (walletLoaded === false) {
            logMessage('ERROR: Wallet not loaded. ');
            return 0;
        }
        if (deployPoolSignature === '') {
            logMessage('ERROR: Invalid deployment signature');
            return 0;
        }
        const transactions = await mainConnection.getParsedTransaction(deployPoolSignature, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0,
        });
        const poolKeys = await getPoolKeys(transactions);
        const tokenAccount = (
            await mainConnection.getParsedTokenAccountsByOwner(
                wallet.publicKey,
                {
                    mint: poolKeys.baseMint,
                },
                'processed',
            )
        ).value[0].pubkey;
        const baseTokenBalance = (await mainConnection.getTokenAccountBalance(tokenAccount, 'processed')).value.amount;
        logMessage(`Your base token balance: ${baseTokenBalance}`);
        setAmountIn(baseTokenBalance);

        return Number(baseTokenBalance);
    }

    async function swapCustomPercent(deployPoolSignature: string, percent: number, priorityGas: number) {
        if (walletLoaded === false) {
            logMessage('ERROR: Wallet not loaded. ');
            return '';
        }
        if (deployPoolSignature === '') {
            logMessage('ERROR: Invalid deployment signature');
            return '';
        }
        try {
            const totalBalance = await getBaseTokenBalance(deployPoolSignature);
            const amountToSwap = Math.floor((totalBalance * percent) / 100);
            logMessage(`Swapping ${amountToSwap.toString()}`);
            swapCustom(deployPoolSignature, amountToSwap, priorityGas);
        } catch (error) {
            logMessage(`Failed to swap ${error}`);
        }
    }

    async function burnLpTokenByPoolDeploymentSignature(poolDeploymentSignature: string, burnLpAmount: number) {
        if (poolDeploymentSignature === '') {
            logMessage('Pool deployment signature required! ');
            return;
        }
        if (burnLpAmount === 0) {
            logMessage('Cant burn 0');
            return;
        }

        logMessage(`Burning ${burnLpAmount.toString()} LP tokens `);
        try {
            const MINT_ADDRESS = await getlpMintByDeploymentSignature(poolDeploymentSignature);
            const tokenAccount = (
                await mainConnection.getTokenAccountsByOwner(
                    wallet.publicKey,
                    {
                        mint: MINT_ADDRESS,
                    },
                    'processed',
                )
            ).value[0].pubkey;

            const signature = await burnToken(mainConnection, wallet, wallet, tokenAccount, burnLpAmount, removeLiquidityBurnLpPriorityGas);
            logMessage(`Bundle sent: ${signature}`);
        } catch (error) {
            logMessage(`Failed to burn LP tokens ${error}`);
        }
    }

    return (
        <section>
            <section className='jito-container'>
                <div className='jito-tx'>
                    <input type='checkbox' checked={isJitoTx} onChange={isJitoTxChange} />
                    <p>JITO remove tx</p>
                </div>
                <div className='jito-future'>
                    <div className='jito-tip'>
                        <input type='number' value={jitoTip} onChange={jitoTipChange} />
                        <p>JITO tip SOL</p>
                    </div>
                    <div className='future-deploy'>
                        <input type='checkbox' checked={futureDeploy} onChange={isFutureDeployChange} />
                        <p>Future Deploy</p>
                    </div>
                </div>
            </section>
            <div className='remove-liquidity'>
                <input placeholder='deployPoolSignature' onChange={deployPoolSignatureChange} value={deployPoolSignature}></input>

                <div className='lp-input-container'>
                    <span>LP Token Balance</span>
                    <input placeholder='10000' onChange={removeLpAmountChange} value={removeLpAmount}></input>
                </div>
            </div>
            <div className='number-of-repeats'>
                <p>Number of repeats (spam value):</p> <input placeholder='1' onChange={repeatsNumberChange} value={repeatsNumber}></input>
                <p>Remove Epoch (ms):</p> <input placeholder='1704562643500' onChange={startTimeEpochChange} value={startTimeEpoch}></input>
            </div>
            <section className='remove-lp-container'>
                <div className='remove-lp-buttons'>
                    <button
                        className='button-150'
                        onClick={() =>
                            removeLiquidity(
                                deployPoolSignature,
                                Math.floor((Number(removeLpAmount) * 999) / 1000).toString(),
                                repeatsNumber,
                                startTimeEpoch,
                                removeLiquidityManualRemovePriorityFee,
                            )
                        }
                    >
                        Remove 99.9%
                    </button>
                    <button
                        className='button-150'
                        onClick={() =>
                            removeLiquidity(deployPoolSignature, removeLpAmount, repeatsNumber, startTimeEpoch, removeLiquidityManualRemovePriorityFee)
                        }
                    >
                        Remove 100%
                    </button>

                    <button className='button-250 button-accent' onClick={() => getSignatures(undefined)}>
                        Get Signatures
                    </button>
                    <button className='button-250 button-accent' onClick={() => getBalance(deployPoolSignature)}>
                        Get Wallet LP Balance
                    </button>
                </div>
                <div className='remove-lp-pool-balance'>
                    <button className='button-250' onClick={() => startPoolBalanceScanning(0, BigInt(Number.MAX_VALUE))}>
                        Scan Pool Balance
                    </button>
                    <p className='poolBalance'>Pool: {Number(poolBalance).toFixed(3)}</p>
                    <p className='poolBalance'>Total: {(Number(poolBalance) + totalWalletsBalance / LAMPORTS_PER_SOL).toFixed(3)}</p>
                </div>
                {/*<div className='tokens-value'>*/}
                {/*    <div className='token'>*/}
                {/*        Tokens: {tokensUi} ({tokensPercentageUi}%)*/}
                {/*    </div>*/}
                {/*    <div className='value'>Value: {valueUi} SOL</div>*/}
                {/*</div>*/}
            </section>
            {/*<section className='list'>*/}
            {/*    <select*/}
            {/*        value={connections.findIndex((connection) => connection.rpcEndpoint == holdersConnection.rpcEndpoint)}*/}
            {/*        onChange={(event) => {*/}
            {/*            console.log(Number(event.target.value));*/}
            {/*            setHoldersConnection(connections[Number(event.target.value)]);*/}
            {/*        }}*/}
            {/*    >*/}
            {/*        {connections.map((connection, index) => (*/}
            {/*            <option value={index} key={index}>*/}
            {/*                {new URL(connection.rpcEndpoint).host}*/}
            {/*            </option>*/}
            {/*        ))}*/}
            {/*    </select>*/}
            {/*</section>*/}
            <section className='scanning'>
                <p>Remove at SOL Goal</p>
                <input type='number' onChange={takeProfitValueChange} value={takeProfitValue.toString()}></input>
                <button className='button-200' onClick={() => startPoolBalanceScanning(0, BigInt(takeProfitValue * LAMPORTS_PER_SOL))}>
                    Scan w/Threshold
                </button>
            </section>
            <section className='scanning'>
                <p>Remove After Delay (ms)</p>
                <input onChange={autoRemoveDelayChange} value={autoRemoveDelay}></input>
                <button className='button-200' onClick={() => startPoolBalanceScanning(autoRemoveDelay, BigInt(0))}>
                    Scan & Remove
                </button>
            </section>
            <section className='token-seller-container'>
                <div>
                    <div className='token-seller-buttons'>
                        <button onClick={scanLpBalance}>T. Sell Scan</button>
                        <button className='button-accent' onClick={() => getBaseTokenBalance(deployPoolSignature)}>
                            Update Balance
                        </button>
                    </div>
                    <div className='token-seller'>
                        <p>Tokens</p>
                        <input value={amountIn} onChange={amountInChange}></input>
                        <button onClick={() => swapCustom(deployPoolSignature, amountIn, removeLiquidityManualSwapPriorityGas)}>Sell</button>
                    </div>
                </div>
                <div className='manual-sell-container'>
                    <div className='manual-sell'>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 100, removeLiquidityManualSwapPriorityGas)}>100%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 99, removeLiquidityManualSwapPriorityGas)}>99%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 98, removeLiquidityManualSwapPriorityGas)}>98%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 75, removeLiquidityManualSwapPriorityGas)}>75%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 50, removeLiquidityManualSwapPriorityGas)}>50%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 20, removeLiquidityManualSwapPriorityGas)}>20%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 5, removeLiquidityManualSwapPriorityGas)}>5%</button>
                        <button onClick={() => swapCustomPercent(deployPoolSignature, 1, removeLiquidityManualSwapPriorityGas)}>1%</button>
                    </div>
                </div>
            </section>
            <section>
                <div className='auto-token-sell-after-remove'>
                    <input type={'checkbox'} checked={autoSell} onChange={autoSellChange}></input>
                    <p>Auto Token Sell After Remove</p>
                </div>
                <div className='auto-sell-input-group'>
                    <input type='number' value={autoSellPercent} onChange={autoSellPercentChange}></input>
                    <span>Percent</span> <span>Every</span>
                    <input value={autoSellMs} onChange={autoSellMsChange}></input>
                    <span>(ms)</span>
                    <input value={autoSellRepeats} onChange={autoSellRepeatsChange}></input>
                    <span>Repeats</span>
                    <input onChange={autoSellSpamChange} value={autoSellSpam}></input>
                    <p>spam TX</p>
                </div>
                <div className='multi-sell-container'>
                    <button onClick={() => multiSell(deployPoolSignature, removeLiquidityManualSwapPriorityGas, 0)}>Instant multi Sell</button>
                    <p>Multi sell start (ms)</p>
                    <input onChange={multiSellStartChange} value={multiSellStart}></input>
                    <button onClick={() => multiSell(deployPoolSignature, removeLiquidityTimedManualSwapPriorityGas, multiSellStart)}>
                        Delayed multi sell
                    </button>
                </div>
            </section>
            <section>
                <div className='burn-lp-container'>
                    <label className='burn-confirmation'>
                        <input type='checkbox' checked={burnConfirmation} onChange={burnConfirmationChagne} />
                        MUST CHECK TO BURN
                    </label>
                    <div className='burn-lp'>
                        <input onChange={burnLpAmountChange} value={burnLpAmount}></input>
                        <button
                            className='button-danger'
                            disabled={!burnConfirmation}
                            onClick={() => {
                                burnLpTokenByPoolDeploymentSignature(deployPoolSignature, burnLpAmount);
                            }}
                        >
                            BURN LP
                        </button>
                    </div>
                </div>
            </section>
        </section>
    );
}
